import { CheckboxVisibility, IContextualMenuItem, SelectionMode, IColumn as IFabricColumn } from "office-ui-fabric-react";
import { ISyncVersion, SourceType, SourceType_ } from "../store/ExternalEpmConnectStore";
import { IOrderBy } from "../store/views";
import { IEntityStage, ISubentity } from "./Subentities";
import { Richtext } from "../components/common/inputs/RichTextInput";
import { any } from "prop-types";

export type MaybeDate = Date | null | undefined | string;

export class Dictionary<T> {
    [key: string]: T;
}

export enum Progress {
    NotStarted = 0,
    Completed = 1,
    InProgress = 2,
    OnHold = 3,
    Canceled = 4
}

export enum StageState {
    NA = 0,
    NotStarted = 1,
    Active = 2,
    WaitingForApproval = 3,
    Completed = 4,
    Canceled = 5,
    Suspended = 6
}

export const ActiveStageState = [
    StageState.Active,
    StageState.WaitingForApproval,
    StageState.Suspended,
    StageState.Canceled,
];

export interface IWarning {
    type: string;
    text: string;
}

export class UpdateContext {
    forceWriteHistory?: boolean;
    overrideDuration?: number;
}

export type UpdateOptions = {
    // the change is already shown in the UI (optimistic inline edit):
    // the action must not dispatch loading/updating actions that would blink the grid
    inlineEdit?: boolean;
};

export interface IBaseEntity {
    id: string;
}

export interface IWithArchivation {
    isArchived?: boolean;
}

export interface IWithStartFinishDates {
    StartDate?: string;
    FinishDate?: string;
}

export interface IWithDuration {
    StartDate?: MaybeDate;
    DueDate?: MaybeDate;
    Duration?: number | null;
}

export interface IWithTagsAttrs {
    Tags?: string[];
}

export interface IEntityInfo {
    id: string;
    name: string;
}

export interface IProcessStageInfo extends IEntityInfo  {
    color: string;
    category: PhaseCategory;
}

export interface IRefInfo extends IEntityInfo {
    entityType?: ServerEntityType;
    isDeleted?: boolean;
}

export interface IColorOption {
    name: string;
    color: string;
}

export type IWithCategory<T> = {
    category: T;
};

export type CategoryOption<T> = IWithCategory<T> & IColorOption;

export enum StatusCategory {
    NA = -1,
    Green = 0,
    Amber = 1,
    Red = 2,
    Done = 10
}

export enum PhaseCategory {
    Initiating = 0,
    Planning = 1,
    Executing = 2,
    MonitoringAndControlling = 3,
    Closing = 10,
    Custom = 15,
    Suspended = 20,
    Canceled = 30
}

export enum Impact {
    NA = 0,
    Low = 1,
    Normal = 2,
    Moderate = 3,
    Strong = 4,
    Extreme = 5
}
export interface ImpactConfig { title: string }
export const ImpactConfigMap: { [i: number]: ImpactConfig } =
{
    [Impact.NA]: {
        title: "N/A"
    },
    [Impact.Low]: {
        title: "Low"
    },
    [Impact.Normal]: {
        title: "Normal"
    },
    [Impact.Moderate]: {
        title: "Moderate"
    },
    [Impact.Strong]: {
        title: "Strong"
    },
    [Impact.Extreme]: {
        title: "Extreme"
    }
}

export type CategoryConfig = {
    title: string;
    cssClassName: string;
    order: number;
    color: string;
    iconName: string;
}

export const statusCategoryMap: Record<StatusCategory, CategoryConfig> =
{
    [StatusCategory.NA]: {
        title: "N/A",
        cssClassName: "Gray",
        order: 0,
        color: '#788086',
        iconName: 'Unknown'
    },
    [StatusCategory.Green]: {
        title: "On Track",
        cssClassName: "Green",
        order: 1,
        color: '#008272',
        iconName: 'PPMXArrowHorizontalStatus'
    },
    [StatusCategory.Amber]: {
        title: "At Risk",
        cssClassName: "Amber",
        order: 2,
        color: '#ff8c00',
        iconName: 'PPMXArrowUpStatus',
    },
    [StatusCategory.Red]: {
        title: "Critical",
        cssClassName: "Red",
        order: 3,
        color: '#a80000',
        iconName: 'PPMXArrowDownStatus',
    },
    [StatusCategory.Done]: {
        title: "Done",
        cssClassName: "Blue",
        order: 4,
        color: '#0076d7',
        iconName: 'PPMXSelected',
    }
}

export const StatusCategoryConfigsList = Object.keys(statusCategoryMap).map(_ => {
    const key: number = parseInt(_);
    const item = statusCategoryMap[key];
    return { ...item, status: key }
}).sort((a, b) => a.order - b.order);

export const phaseCategoryMap: Record<PhaseCategory, CategoryConfig> =
{
    [PhaseCategory.Initiating]: {
        title: "Initiating",
        cssClassName: "LightBlue",
        order: 0,
        color: '#00BBF5',
        iconName: 'CRMProcesses',
    },
    [PhaseCategory.Planning]: {
        title: "Planning",
        cssClassName: "Amber",
        order: 1,
        color: '#FF8C00',
        iconName: 'CRMProcesses',
    },
    [PhaseCategory.Executing]: {
        title: "Executing",
        cssClassName: "Green",
        order: 2,
        color: '#008272',
        iconName: 'CRMProcesses',
    },
    [PhaseCategory.MonitoringAndControlling]: {
        title: "Monitoring & Controlling",
        cssClassName: "Purple",
        order: 3,
        color: '#8271EB',
        iconName: 'CRMProcesses',
    },
    [PhaseCategory.Closing]: {
        title: "Closing",
        cssClassName: "Blue",
        order: 4,
        color: '#0076D7',
        iconName: 'CRMProcesses',
    },
    [PhaseCategory.Custom]: {
        title: "Custom",
        cssClassName: "Gray",
        order: 5,
        color: '#788086',
        iconName: 'CRMProcesses',
    },
    [PhaseCategory.Suspended]: {
        title: "Suspended",
        cssClassName: "Gray",
        order: 5,
        color: '#788086',
        iconName: 'CRMProcesses',
    },
    [PhaseCategory.Canceled]: {
        title: "Canceled",
        cssClassName: "Gray",
        order: 6,
        color: '#788086',
        iconName: 'CRMProcesses',
    }
}

export const SystemPhaseCategories = [PhaseCategory.Canceled, PhaseCategory.Suspended];
export const NotSystemPhaseCategories = [
    PhaseCategory.Initiating,
    PhaseCategory.Planning,
    PhaseCategory.Executing,
    PhaseCategory.MonitoringAndControlling,
    PhaseCategory.Closing,
    PhaseCategory.Custom
];

export interface IExtensibleEntity extends IBaseEntity, IWithAttributes {
}

export interface IPatch<T extends IExtensibleEntity> {
    id?: string;
    attributes: Partial<T['attributes']>;
}

export function buildDelta<T extends IExtensibleEntity>(item: IPatch<T>, prevState: T | undefined) {
    item = { id: item.id, attributes: { ...item.attributes } };

    if (prevState) {
        for (const key in item.attributes) {
            if (prevState.attributes[key] === item.attributes[key]) {
                delete (item.attributes as any)[key];
            }
        }
    }

    return item;
}

export interface IEditable {
    isEditable: boolean;
}

export interface IEditableExtensibleEntity extends IExtensibleEntity, IEditable { }

export interface IBlinePlanActual {
    baseline: number;
    plan: number;
    actual: number;
}

export interface IConnected<T> {
    connectionId: string;
    data: T;
}

export interface IUserInfo {
    id: string;
    fullName: string;
    imageId: string;
    // US 15956: per-assignment Units (%) — set only when this IUserInfo is a task assignment (AssignedTo).
    // Null/undefined ⇒ the 100% default. Mirrors server AssignmentInfo.Units.
    units?: number;
}

export interface ICalculation {
    costs: IBlinePlanActual;
    benefits: IBlinePlanActual;
}

export interface IWithName {
    Name: string;
}

export interface IWithManager {
    Manager: IUserInfo[];
}

export interface IWithLayout {
    layoutId?: string;
}

export interface IWithImage {
    imageId?: string;
}

export interface IWithWarnings {
    warnings: IWarning[];
}

export interface IWithChangeHistory {
    changeHistory: IHistoryRow[];
}

export interface IWithStages {
    stages: IEntityStage[];
}

export interface IWithInsightItems{
    insightItems: InsightItem[];
} 

export interface IWithInsights {
    insights: IInsightsData;
}

export interface IWithSourceInfos {
    sourceInfos: ISourceInfo[];
}

export interface IWithKeyResultAssignments {
    keyResults: KeyResultAssignment[];
}

export interface IWithBenefits {
    BenefitsStartDate?: string;
    BenefitsFinishDate?: string;
}

export interface IWithResourcePlan {
    resourceIds: string[];
}

export interface IEditableExtensibleEntityWithResourcePlan extends IExtensibleEntity, IWithResourcePlan, IEditable { }

export interface KeyResultAssignment {
    id: string;

    keyResultId: string;
    objectiveId: string;

    projectStartValue: number;
    projectTargetValue: number;
    currentValue: number;
    progress?: number;

    status: StatusCategory;
    description: Richtext;
}

export enum ProgressCalculationType {
    Effort = 0,
    StoryPoints = 1,
    AverageProgress = 2,
}

export enum TaskType {
    Flexible = 0,
    FixedUnits = 1,
}

export enum ModeSwitchRecalculation {
    KeepEffortAndUnits = 0,
    KeepDurationAndUnits = 1,
    KeepDurationAndEffort = 2,
}

export type ModeSwitchRecalculationInfo = {
    required: boolean;
    taskCount: number;
    allowKeepDurationAndEffort: boolean;
}

export enum EstimatedChargeCalculationMode {
    UseChargeRate = 0,
    UseCostRateTables = 1
}

export interface IInsightsData {
    warningsCalculationDisabled: boolean;
    keyDateStatusCalculationDisabled: boolean;
    taskStatusCalculationDisabled: boolean;
    statusCalculationDisabled: boolean;
    statuses: Dictionary<IInsightsStatusData>;
}

export interface IInsightsStatusData {
    autoValue: StatusCategory;
    autoDescription: string;
    manualValueDueDate?: string;
    trend: StatusTrendType;
    autoCalcDate?: string;
}


export enum StatusTrendType {
    NA = 0,
    Stable = 1,
    Positive = 2,
    Negative = 3
}

export interface IWithAiInsights {
    aiInsights: AiInsightInfo;
}

export interface IWithAttributes {
    attributes: Dictionary<any>;
}

export interface AiInsightInfo {
    isAiInsightsLoading?: boolean;
    suggestedActions: AiSuggestedAction[];
    lastUpdatedDate?: Date;
    isOutdated: boolean;
    isSyncInProgress: boolean;
}

export interface AiSuggestedAction {
    id: string;
    title: string;
    description: Richtext;
    actionType?: SuggestedActionType;
    connectionId?: string;
    rank: number;
    isLiked?: boolean;
    isDisliked?: boolean;
    
    createdDate: string;
    lastModifiedDate?: string;
    color: string;
    iconName: string;
    sectionName: string;
    priority: number;
    createdBy: string;
}

export interface IInsightItemAttrs {
    Name: string;
    Description: Richtext;
    LastModifiedDate: string;
    Priority: number;
    CreatedBy: string;
}

export interface IInsightItemExternalData {
    Color: string;
    IconName: string;
    SectionName?: string;
    ActionType?: SuggestedActionType;
    IsLiked?: boolean;
    isDisliked?: boolean;
}

export interface InsightItem extends ISubentity {    
    id: string;
    attributes: IInsightItemAttrs;
    externalData: IInsightItemExternalData;
}

export enum SuggestedActionType {
    Statuses = 1,
    ThisWeekKeyDates = 2,
    OverdueKeyDates = 3,
    SlippedKeyDates = 4,
    ActiveRisks = 5,
    ActiveIssues = 6,
    PendingChangeRequests = 7,
    SlippedTasks = 8,
    ThisWeekTasks = 9,
    OverdueTasks = 10,
    Budget = 11,
    BudgetActive = 12,
    Effort = 13,
    StoryPoints = 14,
    OverallocatedResources = 16,
    SyncFailed = 17,
}

export const SuggestedActionAttributeNames = {
    STATUS_LABEL: "STATUS_LABEL",
    STATUS_NAME: "STATUS_NAME",
    STATUS_VALUE: "STATUS_VALUE"
}

export const trendIconMap: { [i: number]: string } = {
    [StatusTrendType.NA]: 'Unknown',
    [StatusTrendType.Stable]: 'PPMXArrowHorizontalStatus',
    [StatusTrendType.Positive]: 'PPMXArrowUpStatus',
    [StatusTrendType.Negative]: 'PPMXArrowDownStatus',
};

export const trendTitleMap: { [i: number]: string } = {
    [StatusTrendType.NA]: 'N/A',
    [StatusTrendType.Stable]: 'Stable',
    [StatusTrendType.Positive]: 'Positive',
    [StatusTrendType.Negative]: 'Negative',
};

export enum CopyScheduleState {
    Queued = 0,
    InProgress = 1,
    Succeeded = 2,
    Failed = 3
}
export type CopyScheduleStatus = {
    state: CopyScheduleState,
    error?: string,
    lastUpdated: string,
}
export interface ISourceInfo<T = any> {
    connectionId: string;
    sourceId?: string;
    type: SourceType;
    syncStatus: {
        inProgress: boolean,
        lastException?: string,
        lastSyncDate: string | null,
        warnings: IWarning[],
    };
    copyScheduleStatus?: CopyScheduleStatus;
    syncVersion: ISyncVersion;
    sourceData: T;
}

export const TasksActualVersions: { plannerPlan: number; spoProject: number, vstsProject: number } = {
    plannerPlan: 1,
    spoProject: 1,
    vstsProject: 1
}

export const ppmxTaskConnectionId: string = "00000000-0000-0000-0000-000000000000";
export const EMPTY_GUID = "00000000-0000-0000-0000-000000000000"

export const PpmxConnection: ISourceInfo<undefined> = {
    type: SourceType.Ppmx,
    connectionId: ppmxTaskConnectionId,
    sourceData: undefined,
    syncStatus: { inProgress: false, lastSyncDate: null, warnings: [] },
    syncVersion: { task: 0 }
}

export interface IHistoryRow {
    id: string;
    value: any;
    fieldName: string;
    date: string;
    setBy: IUserInfo | null;
    description: string;
}

export interface IStatusHistoryRow extends IHistoryRow {
    trend: StatusTrendType;
}

export namespace SourceInfo {
    export function getSyncableConnections(sources: ISourceInfo[]): ISourceInfo[] {
        return sources.filter(_ => isSyncable(_));
    }

    export function getDataConnections(sources: ISourceInfo[]): ISourceInfo[] {
        return sources.filter(_ => _.type !== SourceType.O365TeamsChannel);
    }

    export function getSources(sources: ISourceInfo[]): ISourceInfo[] {
        return sources.filter(_ => !isCollaborative(_));
    }

    export function getCollaborative(sources: ISourceInfo[]): ISourceInfo[] {
        return sources.filter(isCollaborative);
    }

    export function isCollaborative(source: ISourceInfo): boolean {
        return !!~SourceType_.collaborative.indexOf(source.type);
    }

    export function isSyncable(source: ISourceInfo): boolean {
        return source.type !== SourceType.File;
    }

    export function syncFailed(source: ISourceInfo[]): boolean {
        return source.some(_ => (!!_.syncStatus.lastException));
    }

    export function getCopyScheduleStatus(sources: ISourceInfo[]): { label: string, error?: string } | undefined {
        const statuses = sources.reduce<CopyScheduleStatus[]>((acc, source) => {
            if (source.copyScheduleStatus) {
                acc.push(source.copyScheduleStatus);
            }
            return acc;
        }, []);
        if (statuses.length === 0) {
            return undefined;
        }

        const failed = statuses.filter(_ => _.state === CopyScheduleState.Failed);
        if (failed.length > 0) {
            return { label: 'Failed', error: failed.map(_ => _.error).filter(_ => !!_).join('\n') };
        }

        return statuses.some(_ => _.state === CopyScheduleState.Queued || _.state === CopyScheduleState.InProgress)
            ? { label: 'In progress' }
            : { label: 'Succeeded' };
    }

    export function getCopyScheduleStatusSources(sources: ISourceInfo[]): (ISourceInfo & { copyScheduleStatus: NonNullable<ISourceInfo['copyScheduleStatus']> })[] {
        return sources.filter((_): _ is ISourceInfo & { copyScheduleStatus: NonNullable<ISourceInfo['copyScheduleStatus']> } => !!_.copyScheduleStatus);
    }

    export function getSyncStatusConfig(isSyncable: boolean, sourceInfos: ISourceInfo[]): { order: number, label: string } | undefined {
        if (sourceInfos.length === 0) {
            return undefined;
        }
        return !isSyncable
            ? syncStatusMap.inProgress
            : (syncFailed(sourceInfos)
                ? syncStatusMap.fail
                : syncStatusMap.success);
    }

    export function getUrl(sourceInfo: ISourceInfo<any>, entityType: EntityType): string | undefined {
        switch (sourceInfo.type) {
            case SourceType.O365Group: return sourceInfo.sourceData.groupUrl;
            case SourceType.VSTS: return sourceInfo.sourceData.projectUrl;
            case SourceType.Jira: return sourceInfo.sourceData.projectUrl;
            case SourceType.File: return sourceInfo.sourceData.fileUrl;
            case SourceType.O365Planner: return sourceInfo.sourceData.planUrl;
            case SourceType.Spo: return sourceInfo.sourceData.projectUrl;
            case SourceType.O365TeamsChannel: return sourceInfo.sourceData.channelUrl;
            case SourceType.MondayCom: return sourceInfo.sourceData.boardUrl;
            case SourceType.Smartsheet:
                if (entityType === EntityType.Project) { return sourceInfo.sourceData.sheetUrl; }
                if (entityType === EntityType.Program) { return sourceInfo.sourceData.workspaceUrl; }
                return undefined;
            case SourceType.P4W: return sourceInfo.sourceData.projectUrl;
        }
        return '';
    }

    export function GetExternalHint(source: ISourceInfo<any>, entityType: EntityType) {
        const data = source.sourceData;
        switch (source.type) {
            case SourceType.O365Planner:
                return `Linked to plan "${data.title}"`;
            case SourceType.O365Group:
                return `Linked to group site "${data.groupName}"`;
            case SourceType.Spo:
                return `Linked to project "${data.projectName}"`;
            case SourceType.Jira:
                if (data.projects.length > 1) {
                    return "Linked to Jira projects";
                }
                if (data.issues?.length === 1) {
                    return `Linked to issue "${data.issues[0].name}" from project "${data.projects[0].name}"`;
                }
                return `Linked to project "${data.projects[0].name}"`;
            case SourceType.VSTS:
                if (data.workItems?.length === 1) {
                    return `Linked to work item "${data.workItems[0].name}" from project "${data.projectName}"`;
                }
                return `Linked to project "${data.projectName}"`;
            case SourceType.O365TeamsChannel: {
                if (data.channelName) {
                    return `Linked to channel "${data.channelName}"`
                }
                return undefined;
            }
            case SourceType.MondayCom: {
                return `Linked to board "${data.boardName}"`;
            }
            case SourceType.Smartsheet: {
                if (entityType === EntityType.Project) { return `Linked to sheet "${data.sheetName}"`; }
                if (entityType === EntityType.Program) { return `Linked to workspace "${data.workspaceName}"`; }
                return undefined;
            }
            case SourceType.MPPFile: {
                return `Linked to project "${data.fileName.split('.')[0]}"`;
            }
            case SourceType.P4W:
                return `Linked to project "${data.projectName}"`;
            default:
                return undefined;
        }
    }
}

type CheckboxOptions = {
    checkboxVisibility?: CheckboxVisibility;
    selectionMode?: SelectionMode;
};

export function getCheckboxOptionsBasedOnCommands(selectionModeCommands: IContextualMenuItem[] | undefined, showSelectedCount?: boolean): CheckboxOptions {
    return updateCheckboxOptions({ checkboxVisibility: undefined, selectionMode: undefined }, selectionModeCommands, showSelectedCount);
}

export function updateCheckboxOptions(props: CheckboxOptions, selectionModeCommands: IContextualMenuItem[] | undefined, showSelectedCount?: boolean): CheckboxOptions {
    return selectionModeCommands !== undefined && selectionModeCommands.length > 0 || showSelectedCount
        ? props
        : { checkboxVisibility: CheckboxVisibility.hidden, selectionMode: SelectionMode.none };
}

const syncStatusMap: Dictionary<{ order: number, label: string }> = {
    fail: { order: 1, label: "Failed" },
    inProgress: { order: 2, label: "In Progress" },
    success: { order: 3, label: "Success" },
}

export enum EntityType {
    Project = 'project',
    Portfolio = 'portfolio',
    Resource = 'resource',
    Challenge = 'challenge',
    Idea = 'idea',
    StrategicPriority = 'strategicpriority',
    Task = 'externaltask',
    Insights = 'insights',
    KeyDate = 'keydate',
    Iteration = 'iteration',
    Risk = 'risk',
    Issue = 'issue',
    LessonLearned = 'lessonlearned',
    ActionItem = 'actionitem',
    SteeringCommittee = 'steeringcommittee',
    KeyDecision = 'keydecision',
    ChangeRequest = 'changerequest',
    MyWork = "mywork",
    Objective = "objective",
    Program = "program",
    Dependency = "dependency",
    Roadmap = "roadmap",
    RoadmapItem = "roadmapitem",
    KeyResult = 'keyresult',
    ArchivedProject = 'archivedproject',
    PurchaseOrder = 'purchaseorder',
    Invoice = 'invoice',
    Deliverable = 'deliverable',
    TimeTrackingEntry = 'timetrackingentry',
    SuggestedTimeTrackingEntry = 'suggestedtimetrackingentry',
    PrivateProject = 'privateproject',
    User = 'user',
    Process = 'process',
    ProcessStage = 'processstage',
    ProjectStage = 'projectstage',
    SuggestedRisk = 'suggestedrisk',
    SuggestedActionItem = 'suggestedactionitem',
    SuggestedKeyDate = 'suggestedkeydate',
    SuggestedIssue = 'suggestedissue',
    SuggestedDeliverable = 'suggesteddeliverable',
    SuggestedChangeRequest = 'suggestedchangerequest',
    SuggestedLessonLearned = 'suggestedlessonlearned',
    InsightItem = 'insightitem',
    TimeReportingPeriod = 'timereportingperiod',
    TimephasedRate = 'timephasedrate',
    TimephasedCost = 'timephasedcost',
    TimephasedMaxUnits = 'timephasedmaxunits',
    TimephasedNonProjectWork = 'timephasednonprojectwork',
    Attachment = 'attachment',
    ApprovalRequest = 'approvalrequest',
    AssignmentByDay = 'assignmentbyday',
    Calendar = 'calendar'
}

export type EntityTypeLabels = {
    singular: string;
    plural: string;
};

export const entityTypeLabelMap: Record<EntityType, EntityTypeLabels> = {
    [EntityType.Project]: { singular: "Project", plural: "Projects" },
    [EntityType.Program]: { singular: "Program", plural: "Programs" },
    [EntityType.Portfolio]: { singular: "Portfolio", plural: "Portfolios" },
    [EntityType.Resource]: { singular: "Resource", plural: "Resources" },
    [EntityType.Challenge]: { singular: "Business Challenge", plural: "Business Challenges" },
    [EntityType.Idea]: { singular: "Idea", plural: "Ideas" },
    [EntityType.StrategicPriority]: { singular: "Strategic Priority", plural: "Strategic Priorities" },
    [EntityType.Task]: { singular: "Task", plural: "Tasks" },
    [EntityType.Insights]: { singular: "Insights", plural: "Insights" },
    [EntityType.KeyDate]: { singular: "Key Date", plural: "Key Dates" },
    [EntityType.Deliverable]: { singular: "Deliverable", plural: "Deliverables" },
    [EntityType.Iteration]: { singular: "Iteration", plural: "Iterations" },
    [EntityType.Risk]: { singular: "Risk", plural: "Risks" },
    [EntityType.Issue]: { singular: "Issue", plural: "Issues" },
    [EntityType.LessonLearned]: { singular: "Lesson Learned", plural: "Lessons Learned" },
    [EntityType.ActionItem]: { singular: "Action Item", plural: "Action Items" },
    [EntityType.SteeringCommittee]: { singular: "Person", plural: "People" },
    [EntityType.KeyDecision]: { singular: "Key Decision", plural: "Key Decisions" },
    [EntityType.ChangeRequest]: { singular: "Change Request", plural: "Change Requests" },
    [EntityType.MyWork]: { singular: "My Work", plural: "My Works" },
    [EntityType.Objective]: { singular: "Objective", plural: "Objectives" },
    [EntityType.Dependency]: { singular: "Dependency", plural: "Dependencies" },
    [EntityType.Roadmap]: { singular: "Roadmap", plural: "Roadmaps" },
    [EntityType.RoadmapItem]: { singular: "Roadmap Item", plural: "Roadmap Items" },
    [EntityType.KeyResult]: { singular: "Key Result", plural: "Key Results" },
    [EntityType.ArchivedProject]: { singular: "Archived Project", plural: "Archived Projects" },
    [EntityType.PurchaseOrder]: { singular: "Purchase Order", plural: "Purchase Orders" },
    [EntityType.Invoice]: { singular: "Invoice", plural: "Invoices" },
    [EntityType.TimeTrackingEntry]: { singular: "Time Tracking Entry", plural: "Time Tracking Entries" },
    [EntityType.SuggestedTimeTrackingEntry]: { singular: "Suggested Time Tracking Entry", plural: "Suggested Time Tracking Entries" },
    [EntityType.PrivateProject]: { singular: "Private Project", plural: "Private Projects" },
    [EntityType.User]: { singular: "User", plural: "Users" },
    [EntityType.Process]: { singular: "Process", plural: "Processes" },
    [EntityType.ProcessStage]: { singular: "Stage", plural: "Stages" },
    [EntityType.ProjectStage]: { singular: "Project Process Stage", plural: "Project Process Stages" },
    [EntityType.SuggestedRisk]: { singular: "Suggested Risk", plural: "Suggested Risks" },
    [EntityType.SuggestedActionItem]: { singular: "Suggested Action Item", plural: "Suggested Action Items" },
    [EntityType.SuggestedKeyDate]: { singular: "Suggested Key Date", plural: "Suggested Key Dates" },
    [EntityType.SuggestedIssue]: { singular: "Suggested Issue", plural: "Suggested Issues" },
    [EntityType.SuggestedChangeRequest]: { singular: "Suggested Change Request", plural: "Suggested Change Requests" },
    [EntityType.SuggestedDeliverable]: { singular: "Suggested Deliverable", plural: "Suggested Deliverables" },
    [EntityType.SuggestedLessonLearned]: { singular: "Suggested Lesson", plural: "Suggested Lessons" },
    [EntityType.InsightItem]: { singular: "Insight Item", plural: "Insight Items" },
    [EntityType.TimeReportingPeriod]: { singular: "Time Reporting Period", plural: "Time Reporting Periods" },
    [EntityType.TimephasedRate]: { singular: "Timephased Rate", plural: "Timephased Rates" },
    [EntityType.TimephasedCost]: { singular: "Timephased Cost", plural: "Timephased Costs" },
    [EntityType.TimephasedMaxUnits]: { singular: "Timephased Max Units", plural: "Timephased Max Units" },
    [EntityType.TimephasedNonProjectWork]: { singular: "Timephased Non-Project Work", plural: "Timephased Non-Project Work" },
    [EntityType.ApprovalRequest]: { singular: "Approval Request", plural: "Approval Requests" },
    [EntityType.AssignmentByDay]: { singular: "Assignment By Day", plural: "Assignment By Day" },
    [EntityType.Attachment]: { singular: "Attachment", plural: "Attachments" },
    [EntityType.Calendar]: { singular: "Calendar", plural: "Calendars" }
};

export enum ServerEntityType {
    Project = 1,
    Portfolio = 2,
    Challenge = 3,
    Idea = 4,
    KeyDate = 5,
    Risk = 6,
    Issue = 7,
    Task = 8,
    Iteration = 9,
    ActionItem = 10,
    Objective = 11,
    Program = 12,
    Dependency = 13,
    Roadmap = 14,
    RoadmapItem = 15,
    Resource = 16,
    LessonLearned = 17,
    KeyDecision = 18,
    ChangeRequest = 19,
    Insights = 20,
    MyWork = 21,
    SteeringCommittee = 22,
    User = 23,
    ArchivedProject = 24,
    PurchaseOrder = 25,
    Invoice = 26,
    Deliverable = 27,
    TimeTrackingEntry = 28,
    SuggestedTimeTrackingEntry = 29,
    Process = 30,
    ProcessStage = 31,
    ProjectStage = 32,
    TimeReportingPeriod = 33,
    TimephasedCost = 34,
    TimephasedRate = 35,
    TimephasedMaxUnits = 36,
    TimephasedNonProjectWork = 37,
    ApprovalRequest = 38,
    Calendar = 39,
}

export const mapServerEntityType: { [key in number]?: EntityType } = {
    [ServerEntityType.Portfolio]: EntityType.Portfolio,
    [ServerEntityType.Program]: EntityType.Program,
    [ServerEntityType.Project]: EntityType.Project,
    [ServerEntityType.KeyDate]: EntityType.KeyDate,
    [ServerEntityType.Challenge]: EntityType.Challenge,
    [ServerEntityType.Idea]: EntityType.Idea,
    [ServerEntityType.Objective]: EntityType.Objective,
    [ServerEntityType.Task]: EntityType.Task,
    [ServerEntityType.Risk]: EntityType.Risk,
    [ServerEntityType.Issue]: EntityType.Issue,
    [ServerEntityType.Iteration]: EntityType.Iteration,
    [ServerEntityType.ActionItem]: EntityType.ActionItem,
    [ServerEntityType.Dependency]: EntityType.Dependency,
    [ServerEntityType.Roadmap]: EntityType.Roadmap,
    [ServerEntityType.RoadmapItem]: EntityType.RoadmapItem,
    [ServerEntityType.Resource]: EntityType.Resource,
    [ServerEntityType.LessonLearned]: EntityType.LessonLearned,
    [ServerEntityType.KeyDecision]: EntityType.KeyDecision,
    [ServerEntityType.ChangeRequest]: EntityType.ChangeRequest,
    [ServerEntityType.Insights]: EntityType.Insights,
    [ServerEntityType.MyWork]: EntityType.MyWork,
    [ServerEntityType.SteeringCommittee]: EntityType.SteeringCommittee,
    [ServerEntityType.ArchivedProject]: EntityType.ArchivedProject,
    [ServerEntityType.PurchaseOrder]: EntityType.PurchaseOrder,
    [ServerEntityType.Invoice]: EntityType.Invoice,
    [ServerEntityType.Deliverable]: EntityType.Deliverable,
    [ServerEntityType.TimeTrackingEntry]: EntityType.TimeTrackingEntry,
    [ServerEntityType.SuggestedTimeTrackingEntry]: EntityType.SuggestedTimeTrackingEntry,
    [ServerEntityType.Process]: EntityType.Process,
    [ServerEntityType.ProcessStage]: EntityType.ProcessStage,
    [ServerEntityType.ProjectStage]: EntityType.ProjectStage,
    [ServerEntityType.TimephasedCost]: EntityType.TimephasedCost,
    [ServerEntityType.TimephasedRate]: EntityType.TimephasedRate,
    [ServerEntityType.TimephasedMaxUnits]: EntityType.TimephasedMaxUnits,
    [ServerEntityType.TimephasedNonProjectWork]: EntityType.TimephasedNonProjectWork,
    [ServerEntityType.ApprovalRequest]: EntityType.ApprovalRequest,
    [ServerEntityType.Calendar]: EntityType.Calendar,
};

export interface LogoConfig { cssClass?: string, label: string, iconName?: string }

export const entityLogoConfig: { [key in EntityType]?: LogoConfig } = {
    [EntityType.Project]: { label: 'Project', cssClass: 'proj-logo', iconName: 'PPMXProject' },
    [EntityType.Program]: { label: 'Program', cssClass: 'prog-logo', iconName: 'PPMXProgram' },
    [EntityType.Portfolio]: { label: 'Portfolio', cssClass: 'pf-logo', iconName: 'PPMXPortfolio' },
    [EntityType.Idea]: { label: 'Idea', cssClass: 'idea-logo' },
    [EntityType.Objective]: { label: 'Objective' },
    [EntityType.Task]: { label: 'Task' },
    [EntityType.KeyDate]: { label: 'Key Date' },
    [EntityType.Challenge]: { label: 'Challenge', cssClass: 'challenge-logo' },
    [EntityType.Roadmap]: { label: 'Roadmap', cssClass: 'roadmap-logo' },
    [EntityType.Resource]: { label: 'Resource', cssClass: 'res-logo' },
    [EntityType.Process]: { label: 'Process', cssClass: 'process-logo ' },
    [EntityType.Calendar]: { label: 'Calendar', cssClass: 'calendar-logo ' },
}

export enum Quantization {
    days = "d",
    weeks = "w",
    months = "m",
    quarters = "q",
    years = "y"
}

export interface ITimeframe {
    start: Date;
    end: Date;
}

export interface IScalableTimeframe {
    startDate: string;
    endDate: string;
}

export type UserPreferencesSettingsUpdate = {
    parentSettingsPathKeys: string[];
    valueBySettingNameMap: Dictionary<any>;
    immediate?: boolean;
};

export type OriginScale = {
    quantization?: Quantization;
    timeframe?: Partial<ITimeframe>;
}

export enum SortDirection {
    ASC = 0,
    DESC = 1
}

export interface ISortState {
    fieldName: string;
    direction: SortDirection;
}

export type FieldSettings = {
    width: number;
};

export interface IWithActiveSubViewId {
    activeSubViewId?: string;
}

export interface IWithSubViews {
    // subViewId -> columnId
    subViews?: Dictionary<Dictionary<FieldSettings>>;
}

export interface IWithSortBy {
    sortBy?: ISortState;
}

export interface IWithScale {
    quantization?: Quantization;
    timeframe?: Partial<ITimeframe>;
}

export interface DetailsControlSettings extends IWithSubViews, IWithSortBy { }

export interface TimelineControlSettings extends IWithSubViews, IWithSortBy {
    quantization?: Quantization;
    timeframe?: Partial<ITimeframe>;
}

export interface TimelineMapControlSettings extends TimelineControlSettings { }

export const MIN_COLUMN_WIDTH = 100;

export const PANEL_CUSTOM_WIDTH = "456px";
export const LARGE_PANEL_WIDTH = "700px";
export const EXTRA_LARGE_PANEL_WIDTH = "1000px";
export const EXTRA_EXTRA_LARGE_PANEL_WIDTH = "1600px";
export const FULL_SCREEN_PANEL_WIDTH = "100vw";

export const PROGRESS_COMPLETED = 100;

export type ComparerBuilder<T = unknown, TOrderBy = IOrderBy> = (orderBy: TOrderBy) => (a: T, b: T) => number;

export interface ISortingProps<TSort = ISortState | ISortState[]> {
    disabled?: boolean;
    external?: boolean;
    orderBy?: TSort;
    onChange?: (sort: TSort | undefined) => void;
}

export interface IStyleSettingValues {
    largeBars?: boolean;
    showToday?: boolean;
    showRelations?: boolean;
    showBaseline: boolean;
    showActualDates?: boolean;
}

export interface IColumn extends IFabricColumn {
    isSticky?: boolean;
}

export enum SectionNames {
    SUMMARY = "Summary",
    DETAILS = "Details",
    STATUSES = "Statuses",
    PROJECTS = "Projects",
    KEYDATES = "Key dates",
    BUDGET = "Budget",
    STRATEGIC_ALIGNMENT = "Strategic Alignment",
    DECISION_MATRIX = "Decision Matrix",
    TIME = "Time",
    ACTION_ITEMS = "Action items",
    KEY_DECISIONS = "Key Decisions",
    RESOURCE_PLAN = "Resource plan",
    RISKS = "Risks",
    KEYRESULTS = "OKR",
    PROGRAMS = "Programs & Projects",
    KPI = "KPI",
    ISSUES = "Issues",
    CHANGE_REQUESTS = "Change Requests",
    TASKS = "Tasks"
}

export enum Sections {
    ProjectTasksSectionId = "996e861a-2bfb-448f-bcd1-1e51226c14ef",
    RoadMapSectionId = "e7b5eac6-edd0-49a7-95a5-507c58a6493d"
}
export enum AuthProvider {
    o365 = "o365",
    govus_o365 = "govus_o365",
    google = "google",
    email = "email",
    okta = "okta"
}

export const AuthProvidersMap = {
    [AuthProvider.email]: {
        buttonClassName: "email",
        logoClassName: "emaillogo",
        friendlyName: "Email",
        title: undefined
    },
    [AuthProvider.google]: {
        buttonClassName: "google",
        logoClassName: "googlelogo",
        friendlyName: "Google",
        title: undefined
    },
    [AuthProvider.o365]: {
        buttonClassName: "office",
        logoClassName: "microsoft365logo",
        friendlyName: "Microsoft",
        title: undefined
    },
    [AuthProvider.govus_o365]: {
        buttonClassName: "office",
        logoClassName: "microsoft365logo",
        friendlyName: "Microsoft",
        title: undefined
    },
    [AuthProvider.okta]: {
        buttonClassName: "okta",
        logoClassName: "oktalogo",
        friendlyName: "Okta",
        title: "Click here to be redirected to your organization’s Okta page, then log in through Okta using the PPM Express app."
    }
}

export enum CostType {
    Opex = "OPEX",
    Capex = "CAPEX"
}

export type EnumConfigMap = {
    [i: number]: {
        title: string;
        cssClassName: string;
    };
};

export const getPluralSuffix = (count: number) => count === 1 ? "" : "s";
